import {
  Room,
  RoomEvent,
  RemoteParticipant,
  LocalParticipant,
  Track,
  TrackPublication,
  ConnectionState,
  ParticipantEvent,
  DisconnectReason,
  VideoPresets,
  ConnectionQuality,
  Participant,
  type RoomOptions,
  type RoomConnectOptions
} from 'livekit-client'

import { type ParticipantInfo } from '@/pages/CallPage/ParticipantVideo'

const LIVEKIT_URL = 'wss://aira-test-y5vvkb1l.livekit.cloud'
const LIVEKIT_TOKEN = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJleHAiOjE3NjYyNTIzODQsImlkZW50aXR5IjoiS2FzZW0gQWlyYUdPIiwiaXNzIjoiQVBJYWU2ZEtBYXJyR0xMIiwibmJmIjoxNzU2MjUyMzg1LCJzdWIiOiJLYXNlbSBBaXJhR08iLCJ2aWRlbyI6eyJjYW5QdWJsaXNoIjp0cnVlLCJjYW5QdWJsaXNoRGF0YSI6dHJ1ZSwiY2FuU3Vic2NyaWJlIjp0cnVlLCJyb29tIjoiQ1NVTiIsInJvb21Kb2luIjp0cnVlfX0.VPdXYTc3D5uYk23UTc8aoLWgR0pdCP21VwXwZ9CtHwg'

// Global state interface
interface CallState {
  room: Room | null
  connectionState: ConnectionState
  participants: Map<string, ParticipantInfo>
  pinnedParticipant: string | null
  isMicMuted: boolean
  isCameraMuted: boolean
  showDeviceSettings: boolean
  selectedMicId: string
  selectedCamId: string
  selectedSpeakerId: string
  deviceRefreshKey: number
}

// State update callback type
type StateUpdateCallback = (state: CallState) => void

// Global call state
const state: CallState = {
  room: null,
  connectionState: ConnectionState.Disconnected,
  participants: new Map(),
  pinnedParticipant: null,
  isMicMuted: false,
  isCameraMuted: false,
  showDeviceSettings: false,
  selectedMicId: '',
  selectedCamId: '',
  selectedSpeakerId: '',
  deviceRefreshKey: 0,
}

// State update listeners
const stateUpdateListeners: Set<StateUpdateCallback> = new Set()

// Helper function to update state and notify listeners
function updateState(updates: Partial<CallState>) {
  Object.assign(state, updates)
  const newState = { ...state } // Create new reference
  
  stateUpdateListeners.forEach(callback => {
    try {
      callback(newState) // Pass new state object
    } catch (error) {
      console.error('Error in state update callback:', error)
    }
  })
}

// Global call actions following the appActions pattern
export const callActions = {
  // State management
  getState: () => ({ ...state }),
  
  subscribe: (callback: StateUpdateCallback) => {
    stateUpdateListeners.add(callback)
    return () => stateUpdateListeners.delete(callback)
  },

  // Connection management
  connectToRoom: async (): Promise<Room | undefined> => {
    // Prevent multiple connection attempts
    if (state.room && state.connectionState !== ConnectionState.Disconnected) {
      console.log('Already connecting or connected to room, skipping...', {
        connectionState: state.connectionState,
        hasRoom: !!state.room
      })
      return state.room || undefined
    }

    try {
      console.log('Connecting to room...')
      updateState({ connectionState: ConnectionState.Connecting })
      
      const roomOptions: RoomOptions = {
        adaptiveStream: true,
        dynacast: true,
        publishDefaults: {
          videoSimulcastLayers: [VideoPresets.h180, VideoPresets.h360],
          videoCodec: 'vp8',
        },
      }

      const connectOptions: RoomConnectOptions = {
        autoSubscribe: true,
      }

      const newRoom = new Room(roomOptions)
      updateState({ room: newRoom })

      // Set up room event listeners
      newRoom
        .on(RoomEvent.Connected, callActions.handleRoomConnected)
        .on(RoomEvent.Disconnected, callActions.handleRoomDisconnected)
        .on(RoomEvent.ParticipantConnected, callActions.handleParticipantConnected)
        .on(RoomEvent.ParticipantDisconnected, callActions.handleParticipantDisconnected)
        .on(RoomEvent.TrackSubscribed, callActions.handleTrackSubscribed)
        .on(RoomEvent.TrackUnsubscribed, callActions.handleTrackUnsubscribed)
        .on(RoomEvent.TrackPublished, callActions.handleTrackPublished)
        .on(RoomEvent.TrackUnpublished, callActions.handleTrackUnpublished)
        .on(RoomEvent.LocalTrackPublished, callActions.handleLocalTrackPublished)
        .on(RoomEvent.LocalTrackUnpublished, callActions.handleLocalTrackUnpublished)
        .on(RoomEvent.ConnectionQualityChanged, callActions.handleConnectionQualityChanged)
        .on(RoomEvent.Reconnecting, () => updateState({ connectionState: ConnectionState.Reconnecting }))
        .on(RoomEvent.Reconnected, () => updateState({ connectionState: ConnectionState.Connected }))

      await newRoom.connect(LIVEKIT_URL, LIVEKIT_TOKEN, connectOptions)

      // Start with camera and microphone enabled
      await newRoom.localParticipant.enableCameraAndMicrophone()

      return newRoom
    } catch (error) {
      console.error('Failed to connect to room:', error)
      updateState({ connectionState: ConnectionState.Disconnected })
      return undefined
    }
  },

  disconnectRoom: () => {
    if (state.room) {
      state.room.disconnect()
    }
  },

  // Event handlers
  handleRoomConnected: () => {
    console.log('Connected to room')
    updateState({ connectionState: ConnectionState.Connected })

    if (state.room) {
      // Add local participant
      callActions.updateParticipant(state.room.localParticipant)

      // Add existing remote participants
      state.room.remoteParticipants.forEach(participant => {
        callActions.updateParticipant(participant)
      })

      // Set initial pinned participant (local if alone, otherwise first remote)
      const remoteParticipants = Array.from(state.room.remoteParticipants.keys())
      if (remoteParticipants.length === 0) {
        updateState({ pinnedParticipant: state.room.localParticipant.identity })
      } else {
        updateState({ pinnedParticipant: remoteParticipants[0] })
      }
    }
  },

  handleRoomDisconnected: (reason?: DisconnectReason) => {
    console.log('Disconnected from room:', reason)
    updateState({
      connectionState: ConnectionState.Disconnected,
      participants: new Map(),
      pinnedParticipant: null,
    })
  },

  handleParticipantConnected: (participant: RemoteParticipant) => {
    console.log('Participant connected:', participant.identity)

    // Set up participant event listeners
    participant
      .on(ParticipantEvent.TrackSubscribed, () => callActions.updateParticipant(participant))
      .on(ParticipantEvent.TrackUnsubscribed, () => callActions.updateParticipant(participant))
      .on(ParticipantEvent.TrackMuted, () => callActions.updateParticipant(participant))
      .on(ParticipantEvent.TrackUnmuted, () => callActions.updateParticipant(participant))
      .on(ParticipantEvent.IsSpeakingChanged, () => callActions.updateParticipant(participant))
      .on(ParticipantEvent.ConnectionQualityChanged, () => callActions.updateParticipant(participant))

    callActions.updateParticipant(participant)

    // Switch pinned view to remote participant when they join (if local was pinned)
    if (state.room && state.pinnedParticipant === state.room.localParticipant.identity) {
      updateState({ pinnedParticipant: participant.identity })
    }
  },

  handleParticipantDisconnected: (participant: RemoteParticipant) => {
    console.log('Participant disconnected:', participant.identity)

    const newParticipants = new Map(state.participants)
    newParticipants.delete(participant.identity)
    updateState({ participants: newParticipants })

    // If disconnected participant was pinned, switch to local or another remote
    if (state.pinnedParticipant === participant.identity && state.room) {
      const remainingRemotes = Array.from(state.room.remoteParticipants.keys())
      if (remainingRemotes.length > 0) {
        updateState({ pinnedParticipant: remainingRemotes[0] })
      } else {
        updateState({ pinnedParticipant: state.room.localParticipant.identity })
      }
    }
  },

  handleTrackSubscribed: (track: Track, _publication: TrackPublication, participant: RemoteParticipant) => {
    console.log('Track subscribed:', track.kind, participant.identity)
    callActions.updateParticipant(participant)
  },

  handleTrackUnsubscribed: (track: Track, _publication: TrackPublication, participant: RemoteParticipant) => {
    console.log('Track unsubscribed:', track.kind, participant.identity)
    callActions.updateParticipant(participant)
  },

  handleTrackPublished: (publication: TrackPublication, participant: RemoteParticipant) => {
    console.log('Track published:', publication.kind, participant.identity)
    callActions.updateParticipant(participant)
  },

  handleTrackUnpublished: (publication: TrackPublication, participant: RemoteParticipant) => {
    console.log('Track unpublished:', publication.kind, participant.identity)
    callActions.updateParticipant(participant)
  },

  handleLocalTrackPublished: (publication: TrackPublication, participant: LocalParticipant) => {
    console.log('Local track published:', publication.kind)
    callActions.updateParticipant(participant)
  },

  handleLocalTrackUnpublished: (publication: TrackPublication, participant: LocalParticipant) => {
    console.log('Local track unpublished:', publication.kind)
    callActions.updateParticipant(participant)
  },

  handleConnectionQualityChanged: (quality: ConnectionQuality, participant: Participant) => {
    console.log('Connection quality changed:', quality, participant.identity)
    callActions.updateParticipant(participant as LocalParticipant | RemoteParticipant)
  },

  // Participant management
  updateParticipant: (participant: LocalParticipant | RemoteParticipant) => {
    const videoPublication = participant.getTrackPublication(Track.Source.Camera)
    const audioPublication = participant.getTrackPublication(Track.Source.Microphone)

    const participantInfo: ParticipantInfo = {
      participant,
      videoTrack: videoPublication?.track,
      audioTrack: audioPublication?.track,
    }

    const newParticipants = new Map(state.participants)
    newParticipants.set(participant.identity, participantInfo)
    updateState({ participants: newParticipants })
  },

  pinParticipant: (identity: string) => {
    updateState({ pinnedParticipant: identity })
  },

  // Media controls
  toggleMicrophone: async () => {
    if (!state.room) return

    try {
      const enabled = state.room.localParticipant.isMicrophoneEnabled
      await state.room.localParticipant.setMicrophoneEnabled(!enabled)
      updateState({ isMicMuted: enabled })
    } catch (error) {
      console.error('Failed to toggle microphone:', error)
    }
  },

  toggleCamera: async () => {
    if (!state.room) return

    try {
      const enabled = state.room.localParticipant.isCameraEnabled
      await state.room.localParticipant.setCameraEnabled(!enabled)
      updateState({ isCameraMuted: enabled })
    } catch (error) {
      console.error('Failed to toggle camera:', error)
    }
  },

  // Device management
  handleDeviceChange: async (deviceId: string, kind: 'audioinput' | 'videoinput' | 'audiooutput') => {
    if (!state.room) return

    try {
      if (kind === 'audioinput') {
        updateState({ selectedMicId: deviceId })
        await state.room.switchActiveDevice(kind, deviceId)
      } else if (kind === 'videoinput') {
        updateState({ selectedCamId: deviceId })
        await state.room.switchActiveDevice(kind, deviceId)
      } else if (kind === 'audiooutput') {
        updateState({ selectedSpeakerId: deviceId })
        await state.room.switchActiveDevice(kind, deviceId)
      }
      updateState({ deviceRefreshKey: state.deviceRefreshKey + 1 })
    } catch (error) {
      console.error('Failed to switch device:', error)
    }
  },

  // UI state management
  setShowDeviceSettings: (show: boolean) => {
    updateState({ showDeviceSettings: show })
  },

  // Derived state getters
  getPinnedParticipant: () => {
    return state.pinnedParticipant ? state.participants.get(state.pinnedParticipant) : null
  },

  getSmallParticipants: () => {
    return Array.from(state.participants.values()).filter(
      info => info.participant.identity !== state.pinnedParticipant
    )
  },
}

// Make callActions available globally for debugging (like the demo)
if (typeof window !== 'undefined') {
  (window as any).callActions = callActions
}

export type { CallState, StateUpdateCallback }
